import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { DMProvider } from "./contexts/DMContext";
import FloatingDMButton from "./components/dm/FloatingDMButton";
import { Toaster } from "./components/ui/sonner";
import { ImpersonationBanner } from "./components/layout/ImpersonationBanner";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Register, { RegisterByInvite, RegisterByConsultantInvite } from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import RequestsList from "./pages/RequestsList";
import NewRequest from "./pages/NewRequest";
import RequestDetails from "./pages/RequestDetails";
import Notifications from "./pages/Notifications";
import Feasibility from "./pages/Feasibility";
import AdminUsers from "./pages/AdminUsers";
import AdminSpecialties from "./pages/AdminSpecialties";
import AdminConfig from "./pages/AdminConfig";
import SigningPage from "./pages/SigningPage";
import LandingPage from "./pages/LandingPage";
import VerifyPage from "./pages/VerifyPage";
import StudiesList from "./pages/StudiesList";
import StudyWorkspace from "./pages/StudyWorkspace";
import CreateStudy from "./pages/CreateStudy";
import ProfilePage from "./pages/ProfilePage";
import CompanyReps from "./pages/CompanyReps";
import AdminBackup from "./pages/AdminBackup";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Skip loading state if user data was passed from AuthCallback
  if (loading && !location.state?.user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/login" replace />;
  }

  const currentUser = user || location.state?.user;
  if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Router component
function AppRouter() {
  return (
    <Routes>
      {/* Public Verification Page - No auth required */}
      <Route path="/verify/:verificationId" element={<VerifyPage />} />

      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />

      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPassword />
        </PublicRoute>
      } />

      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />

      <Route path="/register/invite/:token" element={
        <PublicRoute>
          <RegisterByInvite />
        </PublicRoute>
      } />

      <Route path="/register/consultant/:token" element={
        <PublicRoute>
          <RegisterByConsultantInvite />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />

      <Route path="/studies" element={
        <ProtectedRoute>
          <StudiesList />
        </ProtectedRoute>
      } />

      <Route path="/studies/new" element={
        <ProtectedRoute>
          <CreateStudy />
        </ProtectedRoute>
      } />

      <Route path="/studies/:id" element={
        <ProtectedRoute>
          <StudyWorkspace />
        </ProtectedRoute>
      } />

      <Route path="/requests" element={
        <ProtectedRoute>
          <RequestsList />
        </ProtectedRoute>
      } />


      <Route path="/requests/new" element={
        <ProtectedRoute allowedRoles={['COMPANY_REP', 'COORDINATOR']}>
          <NewRequest />
        </ProtectedRoute>
      } />

      <Route path="/requests/:id" element={
        <ProtectedRoute>
          <RequestDetails />
        </ProtectedRoute>
      } />

      <Route path="/requests/:id/sign" element={
        <ProtectedRoute>
          <SigningPage />
        </ProtectedRoute>
      } />

      <Route path="/notifications" element={
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      } />

      <Route path="/feasibility" element={
        <ProtectedRoute allowedRoles={['CONSULTANT']}>
          <Feasibility />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['ADMIN', 'CRD', 'CHAIRMAN']}>
          <AdminUsers />
        </ProtectedRoute>
      } />

      <Route path="/company/reps" element={
        <ProtectedRoute allowedRoles={['COMPANY_REP']}>
          <CompanyReps />
        </ProtectedRoute>
      } />

      <Route path="/admin/specialties" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <AdminSpecialties />
        </ProtectedRoute>
      } />

      <Route path="/admin/config" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <AdminConfig />
        </ProtectedRoute>
      } />

      <Route path="/admin/backup" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <AdminBackup />
        </ProtectedRoute>
      } />

      {/* Default: public landing page — authenticated users go straight to dashboard */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <DMProvider>
          <BrowserRouter>
            <ImpersonationBanner />
            <AppRouter />
            <FloatingDMButton />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </DMProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
